import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Download, Save, ArrowLeft, Trash2, Edit2 } from 'lucide-react';
import api from '../../services/api';
import { getReport, saveReport, addExpenseItem, updateExpenseItem, removeExpenseItem, submitReport, approveReport, rejectReport, getAuditTrail } from '../../services/expenseService';
import ExpenseItemForm from '../../components/expense/ExpenseItemForm';

const ExpenseReportForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [report, setReport] = useState({
        title: '',
        dateRange: {
            start: new Date().toISOString().split('T')[0],
            end: new Date().toISOString().split('T')[0]
        },
        purpose: '',
        items: [],
        totalAmount: 0,
        status: 'DRAFT',
        currency: 'INR'
    });
    const [auditTrail, setAuditTrail] = useState([]);
    const [showItemForm, setShowItemForm] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [approvalComment, setApprovalComment] = useState('');
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [approvalAction, setApprovalAction] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [baseCurrency, setBaseCurrency] = useState('INR');
    const [orgName, setOrgName] = useState('PATHtoIP');

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const res = await api.get('/auth/config');
                setBaseCurrency(res.data.baseCurrency || 'INR');
                setOrgName(res.data.orgName || 'PATHtoIP');
            } catch (err) {
                console.error('Failed to fetch config', err);
            }
        };
        fetchConfig();
    }, []);

    useEffect(() => {
        if (id) {
            loadReport(id);
            loadAuditTrail(id);
        }
    }, [id]);

    const loadReport = async (reportId) => {
        const existing = await getReport(reportId);
        if (existing) {
            const uiReport = {
                ...existing,
                dateRange: {
                    start: existing.startDate ? existing.startDate.split('T')[0] : '',
                    end: existing.endDate ? existing.endDate.split('T')[0] : ''
                },
                items: existing.items || []
            };
            setReport(uiReport);
        } else {
            navigate('/tools/expenses');
        }
    };

    const loadAuditTrail = async (reportId) => {
        try {
            const trails = await getAuditTrail(reportId);
            setAuditTrail(trails);
        } catch (error) {
            console.error('Failed to load audit trail', error);
        }
    };

    const handleSaveReport = async () => {
        if (!report.title) {
            alert('Please enter a report title');
            return;
        }

        setIsSaving(true);
        const apiData = {
            ...report,
            startDate: report.dateRange.start,
            endDate: report.dateRange.end
        };

        try {
            const saved = await saveReport(apiData);
            if (saved && !id) {
                navigate(`/tools/expenses/${saved.id}`);
            } else if (saved) {
                loadReport(saved.id);
                // alert('Report saved successfully!');
            }
        } catch (error) {
            console.error('Save report error:', error);
            alert('Failed to save report. Please ensure all database migrations are applied.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddExpense = () => {
        setEditingItem(null);
        setShowItemForm(true);
    };

    const handleEditExpense = (item) => {
        setEditingItem(item);
        setShowItemForm(true);
    };

    const handleSaveExpense = async (itemData) => {
        let updatedReport;
        try {
            if (editingItem) {
                updatedReport = await updateExpenseItem(report.id, editingItem.id, itemData, report.items);
            } else {
                if (report.id) {
                    updatedReport = await addExpenseItem(report.id, itemData, report.items);
                } else {
                    const newItem = { ...itemData, id: Date.now().toString(), temp: true };
                    const updatedItems = [...report.items, newItem];
                    setReport(prev => ({
                        ...prev,
                        items: updatedItems,
                        totalAmount: updatedItems.reduce((sum, item) => sum + (parseFloat(item.convertedAmount || item.amount) || 0), 0)
                    }));
                    setShowItemForm(false);
                    setEditingItem(null);
                    return;
                }
            }
        } catch (error) {
            console.error('Save expense item error:', error);
            alert('Failed to save expense item. Using local state fallback.');
            // Fallback for draft reports not yet in DB
            if (!report.id) {
                 const newItem = { ...itemData, id: Date.now().toString(), temp: true };
                 const updatedItems = [...report.items, newItem];
                 setReport(prev => ({
                     ...prev,
                     items: updatedItems,
                     totalAmount: updatedItems.reduce((sum, item) => sum + (parseFloat(item.convertedAmount || item.amount) || 0), 0)
                 }));
            }
        }

        if (updatedReport) {
            const uiReport = {
                ...updatedReport,
                dateRange: {
                    start: updatedReport.startDate ? updatedReport.startDate.split('T')[0] : '',
                    end: updatedReport.endDate ? updatedReport.endDate.split('T')[0] : ''
                },
                items: updatedReport.items || []
            };
            setReport(uiReport);
        }
        setShowItemForm(false);
        setEditingItem(null);
    };

    const handleDeleteExpense = async (itemId) => {
        if (window.confirm('Delete this expense?')) {
            if (report.id) {
                const isTemp = report.items.find(i => i.id === itemId)?.temp;
                if (isTemp) {
                    const updatedItems = report.items.filter(item => item.id !== itemId);
                    setReport(prev => ({
                        ...prev,
                        items: updatedItems,
                        totalAmount: updatedItems.reduce((sum, item) => sum + (parseFloat(item.convertedAmount || item.amount) || 0), 0)
                    }));
                } else {
                    const updatedReport = await removeExpenseItem(report.id, itemId, report.items);
                    if (updatedReport) {
                        const uiReport = {
                            ...updatedReport,
                            dateRange: {
                                start: updatedReport.startDate ? updatedReport.startDate.split('T')[0] : '',
                                end: updatedReport.endDate ? updatedReport.endDate.split('T')[0] : ''
                            },
                            items: updatedReport.items || []
                        };
                        setReport(uiReport);
                    }
                }
            } else {
                const updatedItems = report.items.filter(item => item.id !== itemId);
                setReport(prev => ({
                    ...prev,
                    items: updatedItems,
                    totalAmount: updatedItems.reduce((sum, item) => sum + (parseFloat(item.convertedAmount || item.amount) || 0), 0)
                }));
            }
        }
    };

    const handleExportPDF = async () => {
        try {
            const response = await api.get(`/expenses/${id}/export`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${report.title?.replace(/\s+/g, '_') || 'ExpenseReport'}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Export error:', error);
            alert('Failed to export PDF. Please try again later.');
        }
    };

    const handleSubmitForApproval = async () => {
        if (!window.confirm('Submit this report for approval? You cannot edit it afterwards.')) return;
        setIsSubmitting(true);
        try {
            const updated = await submitReport(id);
            if (updated) {
                loadReport(id);
                loadAuditTrail(id);
                alert('Report submitted successfully!');
            }
        } catch (error) {
            console.error('Submit report error:', error);
            alert('Failed to submit report. Please check server logs.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleApprovalAction = async () => {
        let result;
        if (approvalAction === 'APPROVE') {
            result = await approveReport(id, approvalComment);
        } else if (approvalAction === 'REJECT') {
            result = await rejectReport(id, approvalComment);
        }

        if (result) {
            setShowApprovalModal(false);
            setApprovalComment('');
            loadReport(id);
            loadAuditTrail(id);
        }
    };

    const StatusBadge = ({ status }) => {
        const colors = {
            DRAFT: '#6b7280',
            SUBMITTED: '#eab308',
            PENDING_MANAGER: '#f59e0b',
            PENDING_FINANCE: '#3b82f6',
            APPROVED: '#22c55e',
            REJECTED: '#ef4444',
            PAID: '#10b981'
        };

        return (
            <span style={{
                padding: '0.25rem 0.75rem',
                borderRadius: 9999,
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'white',
                backgroundColor: colors[status] || '#6b7280'
            }}>
                {status?.replace('_', ' ')}
            </span>
        );
    };

    const canEdit = report.status === 'DRAFT' || report.status === 'REJECTED';

    // Get current user for role-based actions
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const isApprover = useMemo(() => {
        if (!report.status) return false;
        if (report.status === 'SUBMITTED') {
            return ['MANAGER', 'HR', 'ADMIN'].includes(currentUser.role);
        }
        if (report.status === 'PENDING_FINANCE') {
            return ['FINANCE', 'ADMIN'].includes(currentUser.role);
        }
        return false;
    }, [report.status, currentUser.role]);

    return (
        <div className="container section">
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                <div style={{ marginBottom: '2rem' }}>
                    <button
                        onClick={() => navigate('/tools/expenses')}
                        className="btn btn-secondary"
                        style={{ marginBottom: '1rem' }}
                    >
                        <ArrowLeft size={16} style={{ marginRight: '0.5rem' }} />
                        Back to Reports
                    </button>

                    <h1 className="gradient-text" style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>
                        {id ? 'Edit Expense Report' : 'New Expense Report'}
                    </h1>
                </div>

                {/* Workflow Section */}
                {id && (
                    <div style={{
                        background: 'white',
                        borderRadius: 'var(--radius)',
                        boxShadow: 'var(--shadow)',
                        padding: '2rem',
                        marginBottom: '2rem'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h3 style={{ marginBottom: '0.5rem' }}>Report Status</h3>
                                <StatusBadge status={report.status} />
                            </div>

                            <div style={{ display: 'flex', gap: '1rem' }}>
                                {canEdit && (
                                    <button onClick={handleSubmitForApproval} disabled={isSubmitting || isSaving} className="btn btn-primary">
                                        {isSubmitting ? 'Submitting...' : 'Submit for Approval'}
                                    </button>
                                )}

                                {isApprover && (
                                    <>
                                        <button
                                            onClick={() => { setApprovalAction('APPROVE'); setShowApprovalModal(true); }}
                                            className="btn btn-primary"
                                            style={{ backgroundColor: '#22c55e' }}
                                        >
                                            Approve
                                        </button>
                                        <button
                                            onClick={() => { setApprovalAction('REJECT'); setShowApprovalModal(true); }}
                                            className="btn btn-secondary"
                                            style={{ color: '#ef4444', borderColor: '#ef4444' }}
                                        >
                                            Reject
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        {auditTrail.length > 0 && (
                            <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--border)' }}>
                                <h4 style={{ marginBottom: '1rem' }}>Approval History</h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {auditTrail.map((trail) => (
                                        <div key={trail.id} style={{ padding: '1rem', background: '#f8fafc', borderRadius: '0.5rem', fontSize: '0.9rem' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                                <span style={{ fontWeight: 600 }}>{trail.approver?.name} ({trail.approver?.role})</span>
                                                <span style={{ color: 'var(--text-muted)' }}>{new Date(trail.createdAt).toLocaleString()}</span>
                                            </div>
                                            <div style={{ color: trail.status === 'APPROVED' ? '#22c55e' : trail.status === 'REJECTED' ? '#ef4444' : '#6b7280' }}>
                                                Status: {trail.status}
                                            </div>
                                            {trail.comments && (
                                                <div style={{ marginTop: '0.5rem', fontStyle: 'italic' }}>
                                                    "{trail.comments}"
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Report Metadata */}
                <div style={{
                    background: 'white',
                    borderRadius: 'var(--radius)',
                    boxShadow: 'var(--shadow)',
                    padding: '2rem',
                    marginBottom: '2rem'
                }}>
                    <h3 style={{ marginBottom: '1.5rem' }}>Report Details</h3>

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Report Title *</label>
                        <input
                            type="text"
                            value={report.title}
                            onChange={(e) => setReport(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="e.g., Business Trip - NYC"
                            disabled={!canEdit}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius)',
                                fontSize: '1rem'
                            }}
                        />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Start Date</label>
                            <input
                                type="date"
                                value={report.dateRange.start}
                                onChange={(e) => setReport(prev => ({
                                    ...prev,
                                    dateRange: { ...prev.dateRange, start: e.target.value }
                                }))}
                                disabled={!canEdit}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid var(--border)',
                                    borderRadius: 'var(--radius)',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>End Date</label>
                            <input
                                type="date"
                                value={report.dateRange.end}
                                onChange={(e) => setReport(prev => ({
                                    ...prev,
                                    dateRange: { ...prev.dateRange, end: e.target.value }
                                }))}
                                disabled={!canEdit}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid var(--border)',
                                    borderRadius: 'var(--radius)',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Purpose</label>
                        <textarea
                            value={report.purpose}
                            onChange={(e) => setReport(prev => ({ ...prev, purpose: e.target.value }))}
                            placeholder="e.g., Client meeting"
                            rows={3}
                            disabled={!canEdit}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius)',
                                fontSize: '1rem',
                                resize: 'vertical'
                            }}
                        />
                    </div>

                    {canEdit && (
                        <button onClick={handleSaveReport} disabled={isSaving || isSubmitting} className="btn btn-primary">
                            <Save size={16} style={{ marginRight: '0.5rem' }} />
                            {isSaving ? 'Saving...' : 'Save Report'}
                        </button>
                    )}
                </div>

                {/* Expenses Section */}
                <div style={{
                    background: 'white',
                    borderRadius: 'var(--radius)',
                    boxShadow: 'var(--shadow)',
                    padding: '2rem'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                        <h3 style={{ margin: 0 }}>Expenses ({report.items?.length || 0})</h3>
                        <div style={{ display: 'flex', gap: '1rem' }}>
                            {canEdit && (
                                <button onClick={handleAddExpense} className="btn btn-primary">
                                    <Plus size={16} style={{ marginRight: '0.5rem' }} />
                                    Add Expense
                                </button>
                            )}
                            {report.items?.length > 0 && (
                                <button onClick={handleExportPDF} className="btn btn-secondary">
                                    <Download size={16} style={{ marginRight: '0.5rem' }} />
                                    Export PDF
                                </button>
                            )}
                        </div>
                    </div>

                    {report.items?.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-muted)' }}>
                            <p>No expenses added yet</p>
                        </div>
                    ) : (
                        <>
                            <div style={{ marginBottom: '1.5rem' }}>
                                {report.items.map((item) => (
                                    <div key={item.id} style={{
                                        padding: '1rem', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                                        marginBottom: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                                    }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 600 }}>{item.merchant}</div>
                                            <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                <span>{item.category}</span>
                                                <span>•</span>
                                                <span>{item.date ? item.date.split('T')[0] : ''}</span>
                                                <span>•</span>
                                                <span style={{ fontWeight: 600 }}>{item.currency} {parseFloat(item.amount).toFixed(2)}</span>
                                                {item.currency !== (item.baseCurrency || baseCurrency) && item.convertedAmount && (
                                                    <span style={{ fontSize: '0.8rem', fontStyle: 'italic', background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>
                                                        (≈ {item.baseCurrency || baseCurrency} {parseFloat(item.convertedAmount).toFixed(2)})
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {canEdit && (
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <button onClick={() => handleEditExpense(item)} className="btn btn-secondary" style={{ padding: '0.5rem' }}><Edit2 size={16} /></button>
                                                <button onClick={() => handleDeleteExpense(item.id)} className="btn btn-secondary" style={{ padding: '0.5rem', color: '#ef4444' }}><Trash2 size={16} /></button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div style={{
                                padding: '1rem', background: 'var(--card-bg)', borderRadius: 'var(--radius)',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '1.1rem'
                            }}>
                                <span>Total Amount:</span>
                                <span style={{ color: 'var(--primary)' }}>
                                    {report.currency || baseCurrency} {parseFloat(report.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                            </div>
                        </>
                    )}
                </div>

                {canEdit && id && report.items.length > 0 && (
                    <div style={{ marginTop: '2rem', textAlign: 'right' }}>
                        <button onClick={handleSubmitForApproval} disabled={isSubmitting || isSaving} className="btn btn-primary" style={{ padding: '0.75rem 2rem' }}>
                            {isSubmitting ? 'Submitting...' : 'Submit Report for Approval'}
                        </button>
                    </div>
                )}
            </div>

            {/* Approval Modal */}
            {showApprovalModal && (
                <div style={{
                    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1000
                }}>
                    <div style={{ backgroundColor: 'white', borderRadius: '1rem', width: '100%', maxWidth: '24rem', padding: '1.5rem' }}>
                        <h3 style={{ marginBottom: '1rem' }}>{approvalAction === 'APPROVE' ? 'Approve' : 'Reject'} Report</h3>
                        <textarea
                            value={approvalComment}
                            onChange={(e) => setApprovalComment(e.target.value)}
                            style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #ddd', minHeight: '80px', marginBottom: '1.5rem' }}
                            placeholder="Optional comments..."
                        />
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                            <button onClick={() => setShowApprovalModal(false)} className="btn btn-secondary">Cancel</button>
                            <button onClick={handleApprovalAction} className="btn btn-primary" style={{ backgroundColor: approvalAction === 'APPROVE' ? '#22c55e' : '#ef4444' }}>
                                Confirm {approvalAction === 'APPROVE' ? 'Approval' : 'Rejection'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showItemForm && (
                <ExpenseItemForm
                    item={editingItem}
                    onSave={handleSaveExpense}
                    onCancel={() => { setShowItemForm(false); setEditingItem(null); }}
                />
            )}
        </div>
    );
};

export default ExpenseReportForm;
